import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowUpRight,
  Briefcase,
  CircleDashed,
  CheckCircle2,
  StickyNote,
  UserPlus,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useMe } from '@/features/auth/hooks'
import {
  useBulkAssign,
  useBulkEscalate,
  useCloseAlert,
} from '../../hooks/useAlertActions'
import { CloseDialog } from './CloseDialog'
import { CustomerSnapshotCard } from './CustomerSnapshotCard'
import { AlertLinker } from '@/features/cases/components/AlertLinker'
import { AnalystPicker } from '../queue/AnalystPicker'
import { isTerminalStatus } from '../../types'
import type { AlertDetail, AnalystSummary, Resolution } from '../../types'

interface Props {
  alert: AlertDetail
  /** Scrolls the reading column to the notes section. Wired by Detail page. */
  onJumpToAddNote: () => void
}

/**
 * AF-126 — sticky right rail.
 *
 * Holds the action buttons + customer snapshot. The primary action is
 * state-aware: exactly one is visible as the primary CTA at a time so
 * analysts don't have to think about "which button do I click."
 *
 * Assignment goes through `AnalystPicker` so any team member can be
 * picked, not just the current user (per AC: "assign to analyst").
 * "Link to case" / "Create case" both open `AlertLinker` (cases feature).
 */
export function ActionRail({ alert, onJumpToAddNote }: Props) {
  const { data: me } = useMe()
  const navigate = useNavigate()
  const assign = useBulkAssign()
  const close = useCloseAlert()
  const escalate = useBulkEscalate()

  const [closeOpen, setCloseOpen] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkMode, setLinkMode] = useState<'pick' | 'create'>('pick')

  const isMine = me ? alert.assigned_to?.user_id === me.id : false
  const isUnassigned = alert.assigned_to === null
  // "Closed" for the rail's purposes means "no analyst action left" —
  // covers all closed_* terminals *and* merged-into-case.
  const isClosed = isTerminalStatus(alert.status)

  let primary: 'assign' | 'close' | 'escalate' | null
  if (isClosed) primary = null
  else if (isUnassigned) primary = 'assign'
  else if (isMine) primary = 'close'
  else primary = 'escalate'

  const assignToMe = () => {
    if (!me) return
    assign.mutate({ alert_ids: [alert.id], user_id: me.id })
  }

  const onPickAnalyst = (a: AnalystSummary | { user_id: 'me' }) => {
    if (a.user_id === 'me') {
      assignToMe()
    } else {
      const sum = a as AnalystSummary
      assign.mutate({ alert_ids: [alert.id], user_id: sum.user_id })
    }
  }

  const handleEscalate = () => {
    escalate.mutate({
      alert_ids: [alert.id],
      reason: 'Escalated from detail view',
    })
  }

  const handleClose = (resolution: Resolution, reason: string) => {
    // Single alert → the single-alert endpoint (POST /alerts/{id}/close),
    // which enforces the workflow state machine + atomic take-and-close
    // server-side. Bulk-with-one-element bypassed that entirely.
    close.mutate(
      {
        alert_id: alert.id,
        resolution,
        reason: reason || undefined,
      },
      {
        onSuccess: () => {
          setCloseOpen(false)
          if (resolution === 'fraud_confirmed') void navigate('/sars/new')
        },
      },
    )
  }

  return (
    <aside className="flex flex-col gap-3">
      <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3">
        {primary === 'assign' && (
          <Button onClick={assignToMe} disabled={assign.isPending}>
            <UserPlus size={14} /> Assign to me
          </Button>
        )}
        {primary === 'close' && (
          <Button onClick={() => setCloseOpen(true)} disabled={close.isPending}>
            <CheckCircle2 size={14} /> Close
          </Button>
        )}
        {primary === 'escalate' && (
          <Button onClick={handleEscalate} disabled={escalate.isPending}>
            <ArrowUpRight size={14} /> Escalate
          </Button>
        )}

        {/* Secondary actions — always shown so analysts can re-route. */}
        {!isClosed && (
          <AnalystPicker
            triggerLabel={isMine ? 'Reassign…' : 'Assign to…'}
            onPick={onPickAnalyst}
          />
        )}
        {primary !== 'escalate' && !isClosed && (
          <Button
            variant="outline"
            onClick={handleEscalate}
            disabled={escalate.isPending}
          >
            <ArrowUpRight size={14} /> Escalate
          </Button>
        )}
        {primary !== 'close' && !isClosed && (
          <Button
            variant="outline"
            onClick={() => setCloseOpen(true)}
            disabled={close.isPending}
          >
            <CheckCircle2 size={14} /> Close…
          </Button>
        )}
        {isClosed && (
          <Button variant="ghost" disabled>
            <CircleDashed size={14} /> Already closed
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3">
        <Button
          variant="ghost"
          onClick={() => {
            setLinkMode('pick')
            setLinkOpen(true)
          }}
        >
          <Briefcase size={14} /> Link to case
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setLinkMode('create')
            setLinkOpen(true)
          }}
        >
          <Briefcase size={14} /> Create case
        </Button>
        <Button variant="ghost" onClick={onJumpToAddNote}>
          <StickyNote size={14} /> Add note
        </Button>
      </div>

      <CustomerSnapshotCard customer={alert.customer_snapshot} />

      <CloseDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        onConfirm={handleClose}
        pending={close.isPending}
      />

      <AlertLinker
        key={linkMode}
        open={linkOpen}
        onOpenChange={setLinkOpen}
        alertIds={[alert.id]}
        defaultMode={linkMode}
        // Seed the Create panel with this alert's scenario, customer,
        // priority, queue — saves the analyst from retyping context
        // they're already looking at.
        prefillAlerts={[alert]}
        onLinked={({ caseId }) => void navigate(`/cases/${caseId}`)}
      />
    </aside>
  )
}
